// ./core/Web/config/StompConfig.ts

import { getRuntimeWsUrl } from '@config/runtime.config';

// MODIFIED: Added an interface for dynamic path parameters
export interface StompPathParams {
  // NOTE: gameId, tenantId, and tableId come from GAME_CONFIG (e.g., crash/configs/GameConfig.ts, aviator/configs/GameConfig.ts)
  // They are passed through the game state and retrieved in BaseGameController.getStompPathParams()
  gameId: string | number;
  gameSlug: string; // Game slug (e.g., 'crash', 'aviator')
  tenantId: string;
  tableId: string | number;
}

/** Dev uses same-origin /ws/stomp via Vite proxy; prod uses runtime config → VITE_WS_URL → default. */
function resolveDevWsUrl(): string {
  if (typeof window === 'undefined') {
    return 'ws://localhost:5173/ws/stomp';
  }
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/ws/stomp`;
}

export function resolveWsUrl(): string {
  const runtime = getRuntimeWsUrl();
  if (runtime) {
    return runtime;
  }
  if (import.meta.env.DEV) {
    return resolveDevWsUrl();
  }
  const configured = import.meta.env.VITE_WS_URL?.trim();
  if (configured) {
    return configured;
  }
  return 'wss://be.play-studios.ai/ws/stomp';
}

export const STOMP_CONFIG = {
  /** The base WebSocket URL for the STOMP broker. */
  get url() {
    return resolveWsUrl();
  },

  /** Base delay in milliseconds for reconnection attempts. */
  reconnectDelay: 5000,

  /** Timeout in milliseconds for RPC (request-response) calls. */
  rpcTimeoutMs: 5000,

  /**
   * Public topics for broadcast messages from the server.
   * MODIFIED: Topics that depend on game/tenant/table IDs are now functions.
   *
   * NOTE: The gameId parameter comes from GAME_CONFIG.gameId (e.g., crash/configs/GameConfig.ts).
   * The server routes game data (ticks, events) by gameId, not gameSlug.
   * To get separate data streams for different games, use different gameId values in their GameConfig.
   */
  topics: {
    // User-specific topics are dynamic based on game slug
    ack: (p: StompPathParams) => `/user/queue/${p.gameSlug}/ack`,
    error: (p: StompPathParams) => `/user/queue/${p.gameSlug}/error`,
    // REMOVED: The pingAck is sent on the 'tick' topic.
    rpcReplies: '/user/queue/rpc-replies',
    // Game-specific topics are now dynamic functions
    // NOTE: These topics use gameId (from GAME_CONFIG), not gameSlug
    // Server routes by gameId, so games with the same gameId will receive the same data
    events: (p: StompPathParams) =>
      `/topic/v1/t/${p.tenantId}/g/${p.gameId}/tbl/${p.tableId}/events`,
    tick: (p: StompPathParams) =>
      `/topic/v1/t/${p.tenantId}/g/${p.gameId}/tbl/${p.tableId}/tick`,
    pong: (
      p: StompPathParams // MODIFIED: Added dedicated pong topic
    ) => `/topic/v1/t/${p.tenantId}/g/${p.gameId}/tbl/${p.tableId}/pong`,
    // Chat topics - these are topic endpoints for real-time chat messages
    chatHistory: (p: StompPathParams) =>
      `/topic/${p.gameSlug}/v1/t/${p.tenantId}/g/${p.gameId}/tbl/${p.tableId}/chat/all`,
    chatMessages: (p: StompPathParams) =>
      `/topic/${p.gameSlug}/v1/t/${p.tenantId}/g/${p.gameId}/tbl/${p.tableId}/chat/all`,
  },

  /** Prefixes for server-side destinations. */
  prefixes: {
    app: '/app',
    topic: '/topic',
    user: '/user',
  },

  /**
   * Application endpoints for sending messages to the server.
   * MODIFIED: Endpoints are now functions to incorporate dynamic IDs.
   */
  endpoints: {
    joinGame: (p: StompPathParams) =>
      `${p.gameSlug}/v1/t/${p.tenantId}/g/${p.gameId}/tbl/${p.tableId}/join`,
    placeBet: (p: StompPathParams) =>
      `${p.gameSlug}/v1/t/${p.tenantId}/g/${p.gameId}/tbl/${p.tableId}/bet/place`,
    cashout: (p: StompPathParams) =>
      `${p.gameSlug}/v1/t/${p.tenantId}/g/${p.gameId}/tbl/${p.tableId}/cashout/now`,
    cancelBet: (p: StompPathParams) =>
      `${p.gameSlug}/v1/t/${p.tenantId}/g/${p.gameId}/tbl/${p.tableId}/bet/cancel`,
    ping: (p: StompPathParams) =>
      `${p.gameSlug}/v1/t/${p.tenantId}/g/${p.gameId}/tbl/${p.tableId}/ping`, // Added for heartbeat request
    chatSubscribe: (p: StompPathParams) =>
      `${p.gameSlug}/v1/t/${p.tenantId}/g/${p.gameId}/tbl/${p.tableId}/chat/subscribe`,
    chatSend: (p: StompPathParams) =>
      `${p.gameSlug}/v1/t/${p.tenantId}/g/${p.gameId}/tbl/${p.tableId}/chat/send`,
    chatLike: (p: StompPathParams) =>
      `${p.gameSlug}/v1/t/${p.tenantId}/g/${p.gameId}/tbl/${p.tableId}/chat/like`,
  },
} as const;

export type StompConfig = typeof STOMP_CONFIG;
